import dayjs from 'dayjs';

import type { EventFieldConfig, EventFieldTransform } from '../config/tpoGenConfig';
import type { EntityAttribute } from '../types/config';
import { buildAttributesPayload, readAttributesToForm } from './entityAttributes';

export const TRANSFORMS: Record<EventFieldTransform, (v: unknown) => unknown> = {
    trim: (v) => (typeof v === 'string' ? v.trim() : v),
    dayjs: (v) => (v ? dayjs(v as string) : undefined),
};

const RESPONSE_TRANSFORMS: Partial<Record<EventFieldTransform, (v: unknown) => unknown>> = {
    dayjs: TRANSFORMS.dayjs,
};

const ATTR_PREFIX = 'attributes.';

const isAttributePath = (key: string | undefined): boolean =>
    typeof key === 'string' && key.startsWith(ATTR_PREFIX);

export function buildEventPayload(
    entityAttributes: EntityAttribute[],
    fields: EventFieldConfig[],
    formValues: Record<string, unknown>,
): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    for (const field of fields) {
        if (!field.apiKey || !field.name) continue;
        // The nested `attributes` object is owned by the codec below.
        if (isAttributePath(field.apiKey)) continue;
        const raw = formValues[field.name];
        if (raw === undefined || raw === null) continue;
        payload[field.apiKey] = field.transform ? TRANSFORMS[field.transform](raw) : raw;
    }

    payload.attributes = buildAttributesPayload(entityAttributes, fields, formValues, 'event', TRANSFORMS);

    return payload;
}

export function buildEventFormValues(
    entityAttributes: EntityAttribute[],
    fields: EventFieldConfig[],
    data: Record<string, unknown>,
): Record<string, unknown> {
    const values: Record<string, unknown> = {};
    for (const field of fields) {
        if (!field.responseKey || !field.name) continue;
        // The nested `attributes` object is owned by the codec below.
        if (isAttributePath(field.responseKey)) continue;
        const raw = data[field.responseKey];
        if (raw === undefined || raw === null) continue;
        const transformFn = field.responseTransform ? RESPONSE_TRANSFORMS[field.responseTransform] : undefined;
        values[field.name] = transformFn ? transformFn(raw) : raw;
    }

    Object.assign(
        values,
        readAttributesToForm(
            entityAttributes,
            fields,
            data.attributes as Record<string, unknown> | null | undefined,
            'event',
            TRANSFORMS,
        ),
    );

    return values;
}
